import type { ChatItem } from '../../contracts';
import { isEvidenceSubagentToolName } from './components/EvidenceSessionInlineBlock.utils';

/**
 * The Evidence child runs the LATEST turn produced, in transcript order.
 *
 * The turn's action row offers one "X citations" button for the whole answer,
 * so it needs every Evidence child the turn spawned — a follow-up search inside
 * one answer is a second child run, and its sources were cited too. Earlier
 * turns keep their own inline button, which is why this stops at the last user
 * message rather than walking the transcript.
 *
 * An Evidence tool call without a `childAgentRunId` falls back to its own tool
 * call id. Two different things arrive that way and one id serves both: a
 * child that has not started yet, and a session-backed Evidence answer that
 * never spawns a child at all. The second is the reason for the fallback — its
 * answer renders inline and publishes under the tool call id, and without this
 * the turn's action row found nothing and offered no button.
 *
 * The first costs nothing: an id no one has published references under reads
 * as zero citations, and the button renders nothing rather than empty chrome.
 * So this is "the id these citations would be published under", which is
 * answerable before the run exists, rather than "the run id", which is not.
 */
export const extractLatestTurnEvidenceRunIds = (items: readonly ChatItem[]): string[] => {
  const latestTurnStart = items.findLastIndex((item) => item.kind === 'user_message');

  const runIds: string[] = [];
  const seen = new Set<string>();
  for (const item of items.slice(latestTurnStart + 1)) {
    if (item.kind !== 'tool_use') continue;
    if (!isEvidenceSubagentToolName(item.name)) continue;
    const runId = item.childAgentRunId || item.id;
    if (!runId || seen.has(runId)) continue;
    seen.add(runId);
    runIds.push(runId);
  }
  return runIds;
};
